import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, MapPin, Lock, ChevronRight, Loader2, ExternalLink } from 'lucide-react';
import CountryFlag from './CountryFlag';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatPrice = (n, currency) => {
  if (!n) return '—';
  if (currency === 'INR') {
    if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
    if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} Lac`;
    return `₹${n.toLocaleString('en-IN')}`;
  }
  if (currency === 'GBP') return `£${(n / 1000).toFixed(0)}k`;
  if (currency === 'CAD') return `C$${(n / 1000).toFixed(0)}k`;
  return `${n.toLocaleString()}`;
};

const FreshLeadsCarousel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/harvested?limit=12`);
        if (!cancelled) setItems(r.data.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#E07A3A]" />
        </div>
      </section>
    );
  }

  if (items.length === 0) return null;

  const cities = [...new Set(items.map(i => i.city))].slice(0, 4).join(' · ');
  const counts = items.reduce((a, i) => { a[i.country] = (a[i.country] || 0) + 1; return a; }, {});

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-b from-white to-[#F8FAFC] border-y border-slate-100" data-testid="fresh-leads-carousel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#E07A3A] font-bold mb-3">
              <Sparkles className="h-3 w-3" /> Fresh This Week
            </div>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-5xl text-[#1A1A1A] mb-3 leading-[1.05] font-semibold">
              {items.length} new property leads<br />
              <span className="italic text-[#E07A3A]">aggregated &amp; verified</span>
            </h2>
            <p className="text-slate-600 text-sm sm:text-base max-w-2xl">
              Curated by Shopperden AI from public listings across {cities}.
              {counts.IN ? ` ${counts.IN} from India` : ''}
              {counts.UK ? ` · ${counts.UK} from UK` : ''}
              {counts.CA ? ` · ${counts.CA} from Canada` : ''}
              .  Seller contact stays in our Gated Vault until you&apos;re KYC-verified.
            </p>
          </div>
          <Link
            to="/properties"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A1A1A] text-white text-xs uppercase tracking-wider hover:bg-[#E07A3A] transition-colors"
            data-testid="fresh-leads-view-all"
          >
            View all <ChevronRight className="h-3 w-3" />
          </Link>
        </div>

        {/* Horizontal scroll on mobile, grid on desktop */}
        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory lg:grid lg:grid-cols-4 lg:overflow-visible">
          {items.slice(0, 8).map((it) => (
            <div
              key={it.id}
              className="snap-start flex-shrink-0 w-72 lg:w-auto bg-white border border-slate-200 hover:border-[#E07A3A] transition-colors group"
              data-testid={`fresh-lead-${it.id}`}
            >
              <div className="relative h-40 bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden">
                {it.images && it.images[0] ? (
                  <img
                    src={it.images[0]}
                    alt={it.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <CountryFlag code={it.country} className="w-16 h-12 opacity-50" title={it.country} />
                  </div>
                )}

                {/* Subtle dark overlay for watermark legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-black/10 pointer-events-none" />

                {/* Diagonal Shopperden watermark — claims the photo for our brand */}
                <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-white/30 font-['Playfair_Display'] font-bold tracking-[0.25em] text-xl rotate-[-22deg] select-none whitespace-nowrap drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]">
                    SHOPPER<span className="text-[#E07A3A]/80">DEN</span>
                  </span>
                </div>

                <div className="absolute top-3 left-3 flex gap-1.5 flex-col items-start">
                  <span className="text-[10px] uppercase tracking-wider bg-white/95 px-2 py-1 font-bold text-slate-700">
                    {it.verified_badge === 'RERA' && '🛡️ RERA'}
                    {it.verified_badge === 'Propertymark' && '🛡️ Propertymark'}
                    {it.verified_badge === 'CREA' && '🛡️ CREA / MLS'}
                    {(!it.verified_badge || it.verified_badge === 'pending') && '⏳ Pending'}
                  </span>
                  {it.network_opportunity && !it.claimed && (
                    <span className="text-[9px] uppercase tracking-[0.2em] bg-[#E07A3A] text-white px-2 py-1 font-semibold"
                      title="Listed by an agent we've tagged for the Shopperden AI Network — Claim available">
                      Network Opportunity
                    </span>
                  )}
                </div>
                <div className="absolute top-3 right-3 bg-slate-900/85 text-white text-[10px] uppercase tracking-wider px-2 py-1 inline-flex items-center gap-1">
                  <Lock className="h-2.5 w-2.5" /> Vault
                </div>
              </div>

              <div className="p-4">
                <div className="flex items-center gap-1 text-xs text-slate-500 mb-1.5">
                  <MapPin className="h-3 w-3" />
                  <span>{it.city}, {it.country}</span>
                </div>
                <h3 className="text-sm font-bold text-slate-900 mb-2 line-clamp-2 min-h-[2.5rem] group-hover:text-[#E07A3A]">
                  {it.title}
                </h3>
                <p className="text-lg font-bold text-[#E07A3A] mb-3">
                  {formatPrice(it.price, it.currency)}
                </p>
                {it.ai_bullets && it.ai_bullets.length > 0 && (
                  <p className="text-[11px] text-slate-500 line-clamp-2 mb-3">
                    {it.ai_bullets[0]}
                  </p>
                )}
                <Link
                  to={`/register?intent=buyer&lead=${it.id}`}
                  className="block text-center text-[11px] uppercase tracking-wider bg-slate-900 text-white py-2 hover:bg-[#E07A3A] transition-colors"
                  data-testid={`fresh-lead-cta-${it.id}`}
                >
                  Unlock Direct Match
                </Link>
                {it.listing_url || it.source_url ? (
                  <a
                    href={`${API_URL}/api/harvested/${it.id}/visit`}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="mt-1.5 flex items-center justify-center gap-1 text-[10px] tracking-wider text-slate-400 hover:text-[#E07A3A] uppercase py-1.5"
                    data-testid={`fresh-lead-source-${it.id}`}
                    title="Open original listing in new tab"
                  >
                    View source <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-slate-400 italic text-center mt-6 max-w-3xl mx-auto">
          All listings labelled "AI-Sourced Network Listing" until the seller verifies & joins. Shopperden is an AI Real Estate Network — not an estate agent or brokerage.
          A Matchmaking Service Fee unlocks direct match data. We do not facilitate viewings or negotiations.
        </p>
      </div>
    </section>
  );
};

export default FreshLeadsCarousel;
